import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X, Upload, FileText, Eye, EyeOff, RotateCw, RotateCcw, Crop, Scissors, ChevronDown, Calculator, TrendingUp, Wallet, Package, Briefcase } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer, Legend } from "recharts";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { api } from "../lib/api";
import { useFileUpload, UploadProgressBar, UploadDetailedProgress } from "../lib/useFileUpload";
import AdminProjects from "./AdminProjects";
import AdminAssets from "./AdminAssets";
import AdminShareLinks from "./AdminShareLinks";
import AdminApprovals from "./AdminApprovals";
import CropImageModal from "../components/CropImageModal";
import AdminUsers from "./AdminUsers";
import AdminStats from "./AdminStats";
import AdminContacts from "./AdminContacts";
import AdminAuditDownloads from "./AdminAuditDownloads";
import AdminCollegamenti from "./AdminCollegamenti";
import AdminBriefing from "./AdminBriefing";
import AdminFicStats from "./AdminFicStats";
import AdminAiRules from "./AdminAiRules";
import AdminInstallers from "./AdminInstallers";
import StoreInsight from "./StoreInsight";
import SupplierWebsiteFinder from "../components/SupplierWebsiteFinder";

const emptyProduct = { name: "", brand: "", category: "tavoli", description: "", price_net: 0, price_retail: 0, price_offer: "", price_trader: "", purchase_invoice_ref: "", stock: 0, published: true };

const EUR0 = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const PIE_COLORS = ["#c8a85c", "#1d2230", "#8a9a5b", "#a0522d", "#6b7280", "#b08d57", "#4a5568", "#9caf88", "#7b6d8d", "#c17767", "#5f7a6b", "#d4a373", "#556b7d", "#9a8478"];

function OutletValuePie({ data, dataKey, title, icon: Icon }) {
  const rows = (data || []).filter((d) => (d[dataKey] || 0) > 0);
  const total = rows.reduce((s, d) => s + (d[dataKey] || 0), 0);
  return (
    <div className="bg-white border border-ink/10 rounded-sm p-4" data-testid={`outlet-pie-${dataKey}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={14} className="text-gold" />}
        <span className="text-[11px] tracking-[0.2em] uppercase text-slate1">{title}</span>
        <span className="ml-auto text-sm font-semibold text-ink tabular-nums">{EUR0.format(total)}</span>
      </div>
      {rows.length === 0 ? (
        <div className="h-[240px] flex items-center justify-center text-xs text-slate1">Nessun dato (imposta prezzi e stock)</div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={rows} dataKey={dataKey} nameKey="category" cx="50%" cy="50%" outerRadius={78} innerRadius={38} paddingAngle={1}>
              {rows.map((entry, i) => (<Cell key={entry.category} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
            </Pie>
            <ReTooltip formatter={(v, n) => [EUR0.format(v), n]} />
            <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 11, textTransform: "capitalize" }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function OutletStatsPanel({ stats }) {
  if (!stats) return null;
  const margin = stats.total_margin || 0;
  return (
    <div className="mt-8" data-testid="outlet-stats-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="bg-ink text-cream rounded-sm p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-cream/60 inline-flex items-center gap-1.5"><TrendingUp size={12} className="text-gold" /> Valore vendita · IVA escl.</div>
          <div className="text-2xl font-display mt-1 tabular-nums" data-testid="outlet-stats-sale">{EUR0.format(stats.total_sale_vat_excl || 0)}</div>
        </div>
        <div className="bg-white border border-ink/10 rounded-sm p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-1.5"><Wallet size={12} className="text-gold" /> Costo totale</div>
          <div className="text-2xl font-display mt-1 text-ink tabular-nums" data-testid="outlet-stats-cost">{EUR0.format(stats.total_cost || 0)}</div>
        </div>
        <div className="bg-white border border-ink/10 rounded-sm p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-1.5"><Package size={12} className="text-gold" /> Margine potenziale</div>
          <div className={`text-2xl font-display mt-1 tabular-nums ${margin >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="outlet-stats-margin">{EUR0.format(margin)}</div>
          <div className="text-[10px] text-slate1 mt-0.5">{stats.product_count} prodotti · {stats.total_units} pezzi in stock · esclusi invisibili</div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OutletValuePie data={stats.by_category} dataKey="sale_value" title="Vendita per categoria (IVA escl.)" icon={TrendingUp} />
        <OutletValuePie data={stats.by_category} dataKey="cost" title="Costo per categoria" icon={Wallet} />
      </div>
    </div>
  );
}
const PRODUCT_CATEGORIES = [
  "tavoli", "sedie", "divani", "poltrone", "letti", "materassi", "armadi", "librerie",
  "cucine", "elettrodomestici", "tappeti", "illuminazione", "outdoor", "complementi",
];
const emptyBanner = { title: "", subtitle: "", image_url: "", link: "/outlet", kind: "weekly_deal", active: true };
const BANNER_KINDS = [
  { value: "weekly_deal", label: "Offerta Settimanale" },
  { value: "new_brand", label: "Nuovo Brand" },
  { value: "new_pricelist", label: "Nuovo Listino" },
  { value: "new_conditions", label: "Nuove Condizioni" },
  { value: "new_outlet_product", label: "Nuovo Prodotto Outlet" },
];
const BANNER_KIND_LABEL = BANNER_KINDS.reduce((acc, k) => { acc[k.value] = k.label; return acc; }, {});
const MAX_BANNERS = 10;
const emptyFlash = { product_id: "", discount_pct: 20, ends_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16), stock: 5 };

const VALID_ADMIN_TABS = new Set([
  "home", "approvals", "users", "stats", "fic-stats", "ai-rules", "projects", "projects-trader",
  "contacts", "assets", "audit-downloads", "products", "banners", "flash", "share-links", "dropbox-docs",
  "installers", "website-finder", "store-insight",
]);

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [tab, setTabState] = useState(
    initialTab && VALID_ADMIN_TABS.has(initialTab) ? initialTab : "home"
  );
  // Wrapper che mantiene il query param ?tab=... allineato allo stato (deep-link).
  const setTab = useCallback((next) => {
    setTabState(next);
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sincronizza lo stato locale se il query param ?tab= cambia (es. quando l'utente
  // clicca sul bell in navbar mentre è già dentro /admin: navigate('/admin?tab=home')
  // cambia la URL ma non ri-mount il componente).
  useEffect(() => {
    const paramTab = searchParams.get("tab");
    const normalized = paramTab && VALID_ADMIN_TABS.has(paramTab) ? paramTab : "home";
    if (normalized !== tab) {
      setTabState(normalized);
    }
  }, [searchParams, tab]);
  const [pendingCount, setPendingCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [banners, setBanners] = useState([]);
  const [flash, setFlash] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [productStats, setProductStats] = useState(null);
  const [installersUnseen, setInstallersUnseen] = useState(0);
  const [imgCacheBust, setImgCacheBust] = useState(0); // bump per forzare reload <img> dopo rotate
  const [cropTarget, setCropTarget] = useState(null); // {pid, imgId, url} per CropImageModal

  const [pForm, setPForm] = useState(emptyProduct);
  const [pEditing, setPEditing] = useState(null); // product being edited
  const [bForm, setBForm] = useState(emptyBanner);
  const [fForm, setFForm] = useState(emptyFlash);

  async function reload() {
    const [p, b, f] = await Promise.all([
      api.get("/products?include_archived=true"),
      api.get("/banners/all"),
      api.get("/flash-sales"),
    ]);
    setProducts(p.data); setBanners(b.data); setFlash(f.data);
    try {
      const r = await api.get("/admin/users/pending");
      setPendingCount(r.data.length);
    } catch (err) { console.error("[admin] load pending count failed", err); }
    try {
      const rr = await api.get("/admin/products/reservations");
      setReservations(rr.data);
    } catch (err) { console.error("[admin] load reservations failed", err); }
    try {
      const st = await api.get("/admin/products/stats");
      setProductStats(st.data);
    } catch (err) { console.error("[admin] load product stats failed", err); }
  }

  useEffect(() => { reload().catch(() => toast.error("Errore caricamento")); }, []);

  // Badge Installers: conteggio foto/commenti nuovi degli operai fino alla visualizzazione.
  const loadInstallersUnseen = useCallback(async () => {
    try { const { data } = await api.get("/installers/admin/activity/unseen"); setInstallersUnseen(data.count || 0); }
    catch { /* silent */ }
  }, []);
  const markInstallersSeen = useCallback(async () => {
    setInstallersUnseen(0);
    try { await api.post("/installers/admin/activity/seen"); } catch { /* silent */ }
  }, []);
  useEffect(() => {
    if (tab === "installers") {
      markInstallersSeen();
      const t = setInterval(markInstallersSeen, 30000);
      return () => clearInterval(t);
    }
    loadInstallersUnseen();
    const t = setInterval(loadInstallersUnseen, 30000);
    return () => clearInterval(t);
  }, [tab, loadInstallersUnseen, markInstallersSeen]);

  async function markProductSold(pid) {
    if (!window.confirm("Confermare la vendita? Stock sarà decrementato di 1 e la prenotazione rimossa.")) return;
    try {
      await api.post(`/admin/products/${pid}/sold`);
      toast.success("Prodotto venduto · stock aggiornato");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  }

  async function releaseReservation(pid) {
    if (!window.confirm("Rilasciare la prenotazione?")) return;
    try {
      await api.post(`/products/${pid}/release`);
      toast.success("Prenotazione rilasciata");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  }

  async function createProduct(e) {
    e.preventDefault();
    try {
      const payload = {
        ...pForm,
        price_net: Number(pForm.price_net || 0),
        price_retail: Number(pForm.price_retail || 0),
        price_offer: pForm.price_offer === "" ? null : Number(pForm.price_offer),
        price_trader: pForm.price_trader === "" ? null : Number(pForm.price_trader),
        stock: Number(pForm.stock || 0),
      };
      const { data } = await api.post("/products", payload);
      toast.success("Prodotto creato. Aggiungi le immagini e i disegni tecnici dal pannello di modifica.");
      setPForm(emptyProduct);
      setPEditing(data);
      reload();
    } catch { toast.error("Errore creazione prodotto"); }
  }

  async function togglePublished(p) {
    try {
      const { id, images, files, created_at, offer_discount_pct, is_reserved, reserved_until, reserved_by_me, reservation, description_en, category_en, ...rest } = p;
      const payload = {
        ...rest,
        price_net: Number(rest.price_net || 0),
        price_retail: Number(rest.price_retail || 0),
        price_offer: rest.price_offer === "" || rest.price_offer == null ? null : Number(rest.price_offer),
        price_trader: rest.price_trader === "" || rest.price_trader == null ? null : Number(rest.price_trader),
        stock: Number(rest.stock || 0),
        published: p.published === false,
      };
      await api.put(`/products/${id}`, payload);
      toast.success(p.published === false ? "Prodotto pubblicato su Outlet" : "Prodotto spostato in archivio");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento visibilità"); }
  }

  async function saveEditingProduct() {
    if (!pEditing) return;
    try {
      const { id, images, files, created_at, offer_discount_pct, ...rest } = pEditing;
      const payload = {
        ...rest,
        price_net: Number(rest.price_net || 0),
        price_retail: Number(rest.price_retail || 0),
        price_offer: rest.price_offer === "" || rest.price_offer == null ? null : Number(rest.price_offer),
        price_trader: rest.price_trader === "" || rest.price_trader == null ? null : Number(rest.price_trader),
        stock: Number(rest.stock || 0),
      };
      const { data } = await api.put(`/products/${id}`, payload);
      toast.success("Prodotto aggiornato");
      setPEditing(data);
      reload();
    } catch { toast.error("Errore salvataggio"); }
  }

  async function uploadProductImage(pid, file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/products/${pid}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Immagine caricata");
      const fresh = (await api.get(`/products/${pid}`)).data;
      setPEditing(fresh);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    }
  }

  // Upload multi-immagine prodotto (parallelo, drag&drop, max 5)
  const imgUpload = useFileUpload({
    uploadOne: useCallback(async (file, onProgress) => {
      if (!pEditing?.id) throw new Error("Nessun prodotto in modifica");
      if (!file.type.startsWith("image/")) throw new Error("non è un'immagine");
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/products/${pEditing.id}/images`, fd, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (onProgress) onProgress(
            evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0,
            evt.loaded || 0,
            evt.total || file.size,
          );
        },
      });
    }, [pEditing?.id]),
    onAfter: useCallback(async () => {
      if (pEditing?.id) {
        const fresh = (await api.get(`/products/${pEditing.id}`)).data;
        setPEditing(fresh);
        reload();
      }
    }, [pEditing?.id]),
    successLabel: "immagine caricata",
    successLabelPlural: "immagini caricate",
  });

  // Upload multi-PDF "disegni tecnici / schede" prodotto (parallelo, drag&drop)
  const docUpload = useFileUpload({
    uploadOne: useCallback(async (file, onProgress) => {
      if (!pEditing?.id) throw new Error("Nessun prodotto in modifica");
      const fd = new FormData();
      fd.append("file", file);
      // Usa il nome file (senza estensione) come titolo: niente prompt blocking per upload massivo
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      fd.append("admin_only", "false");
      await api.post(`/products/${pEditing.id}/files`, fd, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (onProgress) onProgress(
            evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0,
            evt.loaded || 0,
            evt.total || file.size,
          );
        },
      });
    }, [pEditing?.id]),
    onAfter: useCallback(async () => {
      if (pEditing?.id) {
        const fresh = (await api.get(`/products/${pEditing.id}`)).data;
        setPEditing(fresh);
        reload();
      }
    }, [pEditing?.id]),
    successLabel: "documento caricato",
    successLabelPlural: "documenti caricati",
  });

  // Upload "documenti admin riservati" prodotto (fatture, conferme d'ordine,
  // disegni interni, ecc.). Visibili SOLO ad admin — filtrati via backend e
  // mai esposti a trader/studio/real_estate.
  const adminDocUpload = useFileUpload({
    uploadOne: useCallback(async (file, onProgress) => {
      if (!pEditing?.id) throw new Error("Nessun prodotto in modifica");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      fd.append("admin_only", "true");
      await api.post(`/products/${pEditing.id}/files`, fd, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (onProgress) onProgress(
            evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0,
            evt.loaded || 0,
            evt.total || file.size,
          );
        },
      });
    }, [pEditing?.id]),
    onAfter: useCallback(async () => {
      if (pEditing?.id) {
        const fresh = (await api.get(`/products/${pEditing.id}`)).data;
        setPEditing(fresh);
        reload();
      }
    }, [pEditing?.id]),
    successLabel: "documento riservato caricato",
    successLabelPlural: "documenti riservati caricati",
  });

  async function deleteProductImage(pid, imgId) {
    try {
      await api.delete(`/products/${pid}/images/${imgId}`);
      const fresh = (await api.get(`/products/${pid}`)).data;
      setPEditing(fresh);
      reload();
    } catch { toast.error("Errore eliminazione"); }
  }

  async function reorderProductImages(pid, order) {
    try {
      await api.put(`/products/${pid}/images/reorder`, { order });
      const fresh = (await api.get(`/products/${pid}`)).data;
      setPEditing(fresh);
      reload();
    } catch { toast.error("Errore riordinamento"); }
  }

  async function rotateProductImage(pid, imgId, direction) {
    try {
      await api.post(`/products/${pid}/images/${imgId}/rotate?direction=${direction}`);
      // refresh + busta cache browser sull'<img> (force re-fetch dello stesso URL)
      const fresh = (await api.get(`/products/${pid}`)).data;
      setPEditing(fresh);
      // Aggiunge cache-busting alle img query string per forzare il browser a ricaricare
      setImgCacheBust(Date.now());
      reload();
    } catch { toast.error("Rotazione fallita"); }
  }

  async function removeBgProductImage(pid, imgId, background = "white") {
    // Disabilitato temporaneamente in produzione: il modello U2NET (~170MB) della
    // libreria rembg viene scaricato al primo uso e blocca il worker uvicorn per
    // 30-90 secondi durante il download → sito non risponde. Riabilitare quando
    // pre-caricato a build time o spostato su worker separato.
    void pid; void imgId; void background;
    toast.error("Rimozione sfondo temporaneamente non disponibile");
  }

  async function autoTrimProductImage(pid, imgId) {
    const loadingToast = toast.loading("Rilevamento margini in corso…");
    try {
      const r = await api.post(`/products/${pid}/images/${imgId}/auto-trim`);
      toast.dismiss(loadingToast);
      const { trimmed_pct, old_size, new_size } = r.data || {};
      toast.success(`Margini rimossi: ${trimmed_pct}% (${old_size?.join("×")} → ${new_size?.join("×")})`);
      const fresh = (await api.get(`/products/${pid}`)).data;
      setPEditing(fresh);
      setImgCacheBust(Date.now());
      reload();
    } catch (e) {
      toast.dismiss(loadingToast);
      const msg = e?.response?.data?.detail || "Auto-trim fallito";
      toast.message(msg);
    }
  }

  async function delProduct(id) {
    await api.delete(`/products/${id}`);
    toast.success("Eliminato"); reload();
  }

  async function createBanner(e) {
    e.preventDefault();
    if (!bForm.image_id && !bForm.image_url) {
      toast.error("Carica un'immagine o inserisci un URL");
      return;
    }
    try {
      await api.post("/banners", bForm);
      toast.success("Banner creato");
      setBForm(emptyBanner); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore banner"); }
  }

  async function uploadBannerImage(file) {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Seleziona un file immagine"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/banners/upload-image", fd);
      setBForm((prev) => ({ ...prev, image_id: data.id, image_url: "" }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload immagine");
    }
  }

  async function delBanner(id) {
    await api.delete(`/banners/${id}`); toast.success("Eliminato"); reload();
  }

  async function createFlash(e) {
    e.preventDefault();
    try {
      await api.post("/flash-sales", { ...fForm, discount_pct: Number(fForm.discount_pct), stock: Number(fForm.stock), ends_at: new Date(fForm.ends_at).toISOString() });
      toast.success("Flash sale creata");
      setFForm(emptyFlash); reload();
    } catch { toast.error("Errore flash sale"); }
  }

  async function delFlash(id) {
    await api.delete(`/flash-sales/${id}`); toast.success("Eliminato"); reload();
  }

  return (
    <div className="pt-28 pb-20 bg-cream2 min-h-[100svh]" data-testid="admin-page">
      <div className="max-w-[1760px] mx-auto px-6 lg:px-10">
        {/* Header e KPI top rimossi su richiesta: le tab restano fisse in alto */}

        <div className="sticky top-[64px] z-30 bg-cream2 flex flex-wrap items-center justify-start gap-x-1 gap-y-1 border-b border-ink/10">
          {[
            { k: "home", l: "Home" },
            { l: "Utenti", items: [
              { k: "users", l: "Categorie" },
              { k: "approvals", l: "Approvazioni", badge: pendingCount },
            ] },
            { l: "Statistiche", items: [
              { k: "stats", l: "Statistiche Hub" },
              { k: "fic-stats", l: "Statistiche FiC" },
            ] },
            { l: "Progetti", items: [
              { k: "projects", l: "Progetti Studi" },
              { k: "projects-trader", l: "Progetti Trader" },
              { k: "contacts", l: "Contatti RE" },
            ] },
            { l: "Asset Fornitori", items: [
              { k: "assets", l: "Listini" },
              { k: "website-finder", l: "Trova Siti Web" },
              { k: "audit-downloads", l: "Registro Download" },
              { k: "ai-rules", l: "Regole AI" },
            ] },
            { l: "Prodotti", items: [
              { k: "products", l: "Outlet", badge: reservations.length },
              { k: "flash", l: "Flash Sales" },
              { k: "banners", l: "Banner" },
              { k: "share-links", l: "Link Pubblici" },
            ] },
            { k: "dropbox-docs", l: "Collegamenti" },
            { k: "store-insight", l: "Store Insight" },
          ].map((t) => {
            if (!t.items) {
              return (
                <button key={t.k} onClick={() => setTab(t.k)} className={`relative px-3 lg:px-4 py-3 text-[11px] tracking-[0.18em] uppercase whitespace-nowrap inline-flex items-center gap-2 ${tab === t.k ? "text-ink border-b-2 border-ink" : "text-slate1 hover:text-ink"}`} data-testid={`admin-tab-${t.k}`}>
                  {t.l}
                  {t.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full" data-testid={`admin-tab-${t.k}-badge`}>
                      {t.badge}
                    </span>
                  )}
                </button>
              );
            }
            const isActive = t.items.some((s) => s.k === tab);
            const groupBadge = t.items.reduce((sum, s) => sum + (s.badge || 0), 0);
            return (
              <DropdownMenu key={t.l}>
                <DropdownMenuTrigger asChild>
                  <button className={`relative px-3 lg:px-4 py-3 text-[11px] tracking-[0.18em] uppercase whitespace-nowrap inline-flex items-center gap-1.5 ${isActive ? "text-ink border-b-2 border-ink" : "text-slate1 hover:text-ink"}`} data-testid={`admin-nav-${t.l.toLowerCase().replace(/\s+/g, "-")}`}>
                    {t.l}
                    {groupBadge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-gold text-cream text-[10px] rounded-full">
                        {groupBadge}
                      </span>
                    )}
                    <ChevronDown size={12} className="opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-white border border-ink/10 rounded-sm shadow-lg min-w-[210px] p-1">
                  {t.items.map((s) => (
                    <DropdownMenuItem
                      key={s.k}
                      onSelect={() => setTab(s.k)}
                      className={`px-3 py-2.5 text-[11px] tracking-[0.15em] uppercase cursor-pointer rounded-sm flex items-center justify-between gap-3 ${tab === s.k ? "bg-cream2 text-ink font-semibold" : "text-slate1 focus:text-ink focus:bg-cream2"}`}
                      data-testid={`admin-tab-${s.k}`}
                    >
                      {s.l}
                      {s.badge > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-gold text-cream text-[10px] rounded-full">
                          {s.badge}
                        </span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
          <Link
            to="/admin/imtask"
            className="relative px-3 lg:px-4 py-3 text-[11px] tracking-[0.18em] uppercase whitespace-nowrap inline-flex items-center gap-1.5 text-slate1 hover:text-ink"
            data-testid="admin-tab-crm"
            title="CRM · Task manager admin"
          >
            <Briefcase size={13} /> CRM
          </Link>
        </div>

        {tab === "products" && (
          <>
            <OutletStatsPanel stats={productStats} />
            {reservations.length > 0 && (
              <div className="mt-8 bg-amber-50 border border-amber-300 rounded-sm overflow-hidden" data-testid="admin-reservations-panel">
                <div className="p-4 border-b border-amber-200 flex items-center justify-between bg-amber-100/60">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-amber-900 font-medium inline-flex items-center gap-2">
                    Prenotazioni attive ({reservations.length})
                  </div>
                  <div className="text-[10px] tracking-[0.15em] uppercase text-amber-800">conferma o lascia scadere · 48h</div>
                </div>
                <div className="divide-y divide-amber-200">
                  {reservations.map((p) => {
                    const r = p.reservation || {};
                    const exp = r.expires_at ? new Date(r.expires_at) : null;
                    const remainingMs = exp ? exp - new Date() : 0;
                    const hrs = Math.max(0, Math.floor(remainingMs / 3600000));
                    const min = Math.max(0, Math.floor((remainingMs % 3600000) / 60000));
                    const acctLabel = { studio: "Studio", trader: "Trader", real_estate: "Real Estate" }[r.account_type] || r.account_type;
                    return (
                      <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`admin-reservation-${p.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-ink font-medium">{p.brand} · {p.name}</div>
                          <div className="text-xs text-slate1 mt-0.5">
                            {r.user_name} ({acctLabel}) · {r.user_email} · {r.studio || "—"}
                          </div>
                          <div className="text-[11px] text-amber-900 mt-1">
                            Scade in <span className="font-semibold">{hrs}h {min}m</span> · {exp ? exp.toLocaleString("it-IT") : "—"}
                          </div>
                        </div>
                        <button
                          onClick={() => markProductSold(p.id)}
                          className="bg-emerald-700 text-cream px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift"
                          data-testid={`admin-reservation-sold-${p.id}`}
                        >
                          Venduto
                        </button>
                        <button
                          onClick={() => releaseReservation(p.id)}
                          className="border border-amber-700 text-amber-900 px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-amber-700 hover:text-cream transition-colors"
                          data-testid={`admin-reservation-release-${p.id}`}
                        >
                          Rilascia
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={createProduct} className="bg-white border border-ink/10 p-6 rounded-sm space-y-3" data-testid="admin-product-form">
              <div className="font-display text-lg text-ink">Nuovo prodotto</div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Nome</label>
                <input required placeholder="Es. Tavolo Eclipse" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Brand</label>
                <input required placeholder="Es. Cassina" value={pForm.brand} onChange={(e) => setPForm({ ...pForm, brand: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Categoria</label>
                <select value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream">
                  {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Descrizione</label>
                <textarea placeholder="Materiali, finiture, dimensioni..." rows={2} value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Prezzo Listino € <span className="text-gold normal-case tracking-normal">(IVA inclusa)</span></label>
                  <input required type="number" min={0} step="0.01" value={pForm.price_retail} onChange={(e) => setPForm({ ...pForm, price_retail: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
                  <div className="text-[10px] text-slate1 mt-0.5">Visibile a tutti</div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Prezzo Offerta € <span className="text-gold normal-case tracking-normal">(IVA inclusa)</span></label>
                  <input type="number" min={0} step="0.01" placeholder="—" value={pForm.price_offer} onChange={(e) => { const v = e.target.value; setPForm({ ...pForm, price_offer: v, price_trader: v === "" || Number(v) <= 0 ? "" : Math.ceil(Number(v) / 1.22) }); }} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="admin-product-price-offer" />
                  <div className="text-[10px] text-slate1 mt-0.5">Studio + Real Estate</div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Prezzo Trader € <span className="text-gold normal-case tracking-normal">(IVA esclusa)</span></label>
                  <input type="number" readOnly value={pForm.price_trader} placeholder="—" className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream/60 text-slate-600 cursor-not-allowed" data-testid="admin-product-price-trader" title="Calcolato automaticamente: Offerta scorporata IVA 22%, arrotondata all'euro superiore" />
                  <div className="text-[10px] text-slate1 mt-0.5">Auto: Offerta ÷ 1,22 · Solo Trader</div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-gold">Prezzo Netto € <span className="text-[9px]">admin</span></label>
                  <input required type="number" min={0} step="0.01" value={pForm.price_net} onChange={(e) => setPForm({ ...pForm, price_net: e.target.value })} className="mt-1 w-full border border-gold/40 px-3 py-2 rounded-sm bg-cream" />
                  <div className="text-[10px] text-slate1 mt-0.5">Solo Admin (costo)</div>
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-gold">Estremi fattura acquisto <span className="text-[9px]">admin</span></label>
                <input placeholder="Es. INV-2026/123 del 01/02/2026 - Fornitore Cassina" value={pForm.purchase_invoice_ref} onChange={(e) => setPForm({ ...pForm, purchase_invoice_ref: e.target.value })} className="mt-1 w-full border border-gold/40 px-3 py-2 rounded-sm bg-cream" data-testid="admin-product-invoice" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Stock</label>
                <input type="number" min={0} value={pForm.stock} onChange={(e) => setPForm({ ...pForm, stock: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer select-none p-3 border border-ink/15 rounded-sm bg-cream" data-testid="admin-product-published-toggle">
                <input
                  type="checkbox"
                  checked={pForm.published !== false}
                  onChange={(e) => setPForm({ ...pForm, published: e.target.checked })}
                  className="mt-0.5 accent-ink w-4 h-4"
                />
                <span>
                  <span className="text-[11px] tracking-[0.15em] uppercase text-ink font-semibold block">Visibile online su Outlet</span>
                  <span className="text-[11px] text-slate1 block mt-0.5">
                    {pForm.published !== false
                      ? "Il prodotto sarà pubblicato subito su Outlet"
                      : "Solo archivio: non visibile online fino alla pubblicazione"}
                  </span>
                </span>
              </label>
              <div className="text-xs text-slate1">Le immagini e i disegni tecnici (PDF) si caricano dopo la creazione.</div>
              <button className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm inline-flex items-center justify-center gap-2" data-testid="admin-create-product"><Plus size={14} /> Crea prodotto</button>
            </form>

            <div className="lg:col-span-2 bg-white border border-ink/10 rounded-sm">
              <div className="p-4 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">
                  Catalogo ({(() => {
                    const q = productSearch.trim().toLowerCase();
                    if (!q) return products.length;
                    const n = products.filter(p =>
                      (p.name || "").toLowerCase().includes(q) ||
                      (p.brand || "").toLowerCase().includes(q) ||
                      (p.category || "").toLowerCase().includes(q)
                    ).length;
                    return `${n}/${products.length}`;
                  })()})
                </div>
                <div className="relative flex-1 min-w-[200px] max-w-[360px]">
                  <input
                    type="search"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Cerca per nome, marca, categoria…"
                    className="w-full border border-ink/20 pl-9 pr-9 py-2 text-sm rounded-sm bg-cream focus:outline-none focus:border-ink"
                    data-testid="admin-products-search"
                    autoComplete="off"
                  />
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate1 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" /></svg>
                  {productSearch && (
                    <button
                      type="button"
                      onClick={() => setProductSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate1 hover:text-ink"
                      aria-label="Pulisci ricerca"
                      data-testid="admin-products-search-clear"
                    ><X size={14} /></button>
                  )}
                </div>
              </div>
              <div className="divide-y divide-ink/10 max-h-[640px] overflow-auto">
                {(() => {
                  const q = productSearch.trim().toLowerCase();
                  const list = q
                    ? products.filter(p =>
                        (p.name || "").toLowerCase().includes(q) ||
                        (p.brand || "").toLowerCase().includes(q) ||
                        (p.category || "").toLowerCase().includes(q)
                      )
                    : products;
                  if (list.length === 0) {
                    return (
                      <div className="p-8 text-center text-slate1 text-sm">
                        {q ? `Nessun prodotto trovato per "${productSearch}"` : "Nessun prodotto"}
                      </div>
                    );
                  }
                  return list.map(p => {
                  const cover = (p.images && p.images.length > 0) ? `${process.env.REACT_APP_BACKEND_URL}/api/products/images/${p.images[0].id}?v=${p.images[0].size || 0}` : (p.image_url || "");
                  return (
                  <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`admin-product-${p.id}`}>
                    {cover ? <img src={cover} alt={p.name} className="w-16 h-16 object-cover rounded-sm" /> : <div className="w-16 h-16 bg-cream2 rounded-sm flex items-center justify-center text-slate1 text-[10px]">no img</div>}
                    <div className="flex-1">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{p.brand} · {p.category}</div>
                      <div className="text-ink flex items-center gap-2 flex-wrap">
                        {p.name}
                        {p.published === false && (
                          <span className="text-[9px] tracking-[0.15em] uppercase bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5" data-testid={`admin-product-archived-${p.id}`}>Archivio</span>
                        )}
                      </div>
                      <div className="text-xs text-slate1">
                        Listino € {Math.ceil(p.price_retail || 0)}
                        {p.price_offer > 0 && <> · Offerta € {Math.ceil(p.price_offer)}</>}
                        {p.price_trader > 0 && <> · Trader € {Math.ceil(p.price_trader)}</>}
                        · Netto € {Math.ceil(p.price_net || 0)} · Stock {p.stock}
                      </div>
                      <div className="text-[10px] text-slate1">
                        {(p.images || []).length} foto · {(p.files || []).length} disegni
                      </div>
                    </div>
                    <button
                      onClick={() => togglePublished(p)}
                      className={p.published === false ? "text-amber-600 hover:text-ink" : "text-emerald-700 hover:text-ink"}
                      title={p.published === false ? "In archivio — clicca per pubblicare su Outlet" : "Pubblicato su Outlet — clicca per spostare in archivio"}
                      data-testid={`admin-toggle-published-${p.id}`}
                    >{p.published === false ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    <button onClick={() => setPEditing(p)} className="text-slate1 hover:text-ink" title="Modifica" data-testid={`admin-edit-product-${p.id}`}><Pencil size={16} /></button>
                    <button onClick={() => { if (window.confirm("Eliminare il prodotto?")) delProduct(p.id); }} className="text-slate1 hover:text-destructive" title="Elimina" data-testid={`admin-del-product-${p.id}`}><Trash2 size={16} /></button>
                  </div>
                );});
                })()}
              </div>
            </div>

            {pEditing && (
              <div className="fixed inset-0 z-50 bg-ink/40" onClick={() => setPEditing(null)}>
                <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-cream overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-product-edit">
                  <div className="p-6 border-b border-ink/10 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Modifica prodotto</div>
                      <div className="font-display text-xl text-ink">{pEditing.name || "Nuovo"}</div>
                    </div>
                    <button onClick={() => setPEditing(null)} className="text-slate1"><X size={18} /></button>
                  </div>

                  <div className="p-6 space-y-4">
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Nome</label>
                      <input value={pEditing.name} onChange={(e) => setPEditing({ ...pEditing, name: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="edit-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Brand</label>
                        <input value={pEditing.brand} onChange={(e) => setPEditing({ ...pEditing, brand: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Categoria</label>
                        <select value={pEditing.category} onChange={(e) => setPEditing({ ...pEditing, category: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-white">
                          {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Descrizione</label>
                      <textarea rows={3} value={pEditing.description} onChange={(e) => setPEditing({ ...pEditing, description: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Prezzo Listino € <span className="text-gold normal-case tracking-normal">(IVA inclusa)</span></label>
                        <input type="number" step="0.01" value={pEditing.price_retail ?? 0} onChange={(e) => setPEditing({ ...pEditing, price_retail: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="edit-price-retail" />
                        <div className="text-[10px] text-slate1 mt-0.5">Visibile a tutti</div>
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Prezzo Offerta € <span className="text-gold normal-case tracking-normal">(IVA inclusa)</span></label>
                        <input type="number" step="0.01" placeholder="—" value={pEditing.price_offer ?? ""} onChange={(e) => { const v = e.target.value; setPEditing({ ...pEditing, price_offer: v, price_trader: v === "" || Number(v) <= 0 ? "" : Math.ceil(Number(v) / 1.22) }); }} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="edit-price-offer" />
                        <div className="text-[10px] text-slate1 mt-0.5">
                          Studio + Real Estate
                          {pEditing.offer_discount_pct > 0 && <span className="text-gold ml-1">· -{Math.round(pEditing.offer_discount_pct)}%</span>}
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Prezzo Trader € <span className="text-gold normal-case tracking-normal">(IVA esclusa)</span></label>
                        <input type="number" readOnly value={pEditing.price_trader ?? ""} placeholder="—" className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream/60 text-slate-600 cursor-not-allowed" data-testid="edit-price-trader" title="Calcolato automaticamente: Offerta scorporata IVA 22%, arrotondata all'euro superiore" />
                        <div className="text-[10px] text-slate1 mt-0.5">Auto: Offerta ÷ 1,22 · Solo Trader</div>
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-gold">Prezzo Netto € <span className="text-[9px]">admin</span></label>
                        <input type="number" step="0.01" value={pEditing.price_net ?? 0} onChange={(e) => setPEditing({ ...pEditing, price_net: e.target.value })} className="mt-1 w-full border border-gold/40 px-3 py-2 rounded-sm bg-white" data-testid="edit-price-net" />
                        <div className="text-[10px] text-slate1 mt-0.5">Costo</div>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-gold">Estremi fattura acquisto <span className="text-[9px]">admin</span></label>
                      <input placeholder="Es. INV-2026/123 del 01/02/2026 - Fornitore Cassina" value={pEditing.purchase_invoice_ref ?? ""} onChange={(e) => setPEditing({ ...pEditing, purchase_invoice_ref: e.target.value })} className="mt-1 w-full border border-gold/40 px-3 py-2 rounded-sm bg-white" data-testid="edit-invoice" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Stock</label>
                      <input type="number" value={pEditing.stock ?? 0} onChange={(e) => setPEditing({ ...pEditing, stock: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                    </div>
                    <div className="col-span-2">
                      <label className="flex items-start gap-2.5 cursor-pointer select-none p-3 border border-ink/15 rounded-sm bg-white" data-testid="admin-edit-published-toggle">
                        <input
                          type="checkbox"
                          checked={pEditing.published !== false}
                          onChange={(e) => setPEditing({ ...pEditing, published: e.target.checked })}
                          className="mt-0.5 accent-ink w-4 h-4"
                        />
                        <span>
                          <span className="text-[11px] tracking-[0.15em] uppercase text-ink font-semibold block">Visibile online su Outlet</span>
                          <span className="text-[11px] text-slate1 block mt-0.5">
                            {pEditing.published !== false ? "Pubblicato: visibile a tutti su Outlet" : "Solo archivio: non visibile online fino alla pubblicazione"}
                          </span>
                        </span>
                      </label>
                    </div>
                    <button onClick={saveEditingProduct} className="bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="edit-save">Salva modifiche</button>

                    <div className="pt-4 border-t border-ink/10">
                      <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">Immagini ({(pEditing.images || []).length}/5)</div>
                      <div className="text-[10px] text-slate1 mb-2">Trascina per riordinare. La prima è la cover.</div>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        {(pEditing.images || []).map((img, idx) => (
                          <div
                            key={img.id}
                            className="relative group cursor-move"
                            draggable
                            onDragStart={(e) => { e.dataTransfer.setData("text/plain", img.id); e.dataTransfer.effectAllowed = "move"; }}
                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const fromId = e.dataTransfer.getData("text/plain");
                              if (!fromId || fromId === img.id) return;
                              const ids = (pEditing.images || []).map(i => i.id);
                              const fromIdx = ids.indexOf(fromId);
                              const toIdx = ids.indexOf(img.id);
                              if (fromIdx < 0 || toIdx < 0) return;
                              const next = [...ids];
                              const [m] = next.splice(fromIdx, 1);
                              next.splice(toIdx, 0, m);
                              reorderProductImages(pEditing.id, next);
                            }}
                            data-testid={`img-${img.id}`}
                          >
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/products/images/${img.id}${imgCacheBust ? `?v=${imgCacheBust}` : ""}`} alt="" className="w-full h-24 object-cover rounded-sm" />
                            {idx === 0 && (
                              <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-gold text-cream text-[9px] tracking-[0.2em] uppercase rounded-sm">Cover</span>
                            )}
                            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCropTarget({
                                    pid: pEditing.id,
                                    imgId: img.id,
                                    url: `${process.env.REACT_APP_BACKEND_URL}/api/products/images/${img.id}?v=${img.updated_at || img.size || 0}`,
                                  });
                                }}
                                className="p-1 bg-cream/90 text-ink rounded-sm hover:bg-white"
                                title="Ritaglia"
                                data-testid={`crop-${img.id}`}
                              ><Crop size={12} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); autoTrimProductImage(pEditing.id, img.id); }}
                                className="p-1 bg-cream/90 text-ink rounded-sm hover:bg-white"
                                title="Auto-trim margini bianchi/grigi"
                                data-testid={`trim-${img.id}`}
                              ><Scissors size={12} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); rotateProductImage(pEditing.id, img.id, "ccw"); }}
                                className="p-1 bg-cream/90 text-ink rounded-sm hover:bg-white"
                                title="Ruota a sinistra (90°)"
                                data-testid={`rot-ccw-${img.id}`}
                              ><RotateCcw size={12} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); rotateProductImage(pEditing.id, img.id, "cw"); }}
                                className="p-1 bg-cream/90 text-ink rounded-sm hover:bg-white"
                                title="Ruota a destra (90°)"
                                data-testid={`rot-cw-${img.id}`}
                              ><RotateCw size={12} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteProductImage(pEditing.id, img.id); }}
                                className="p-1 bg-cream/90 text-rose-700 rounded-sm hover:bg-white"
                                title="Elimina"
                                data-testid={`del-img-${img.id}`}
                              ><Trash2 size={12} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {(pEditing.images || []).length < 5 ? (
                        <label
                          className={`block border-2 border-dashed ${imgUpload.dragOver ? "border-ink bg-cream2" : "border-ink/20"} p-4 rounded-sm text-center cursor-pointer hover:bg-white transition-colors`}
                          {...imgUpload.handlers}
                        >
                          <Upload size={18} className="mx-auto text-slate1 mb-1" />
                          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">
                            {imgUpload.uploading
                              ? (imgUpload.progress ? `Caricamento ${imgUpload.progress.done}/${imgUpload.progress.total}…` : "Caricamento...")
                              : (imgUpload.dragOver ? "Rilascia qui le immagini" : "Trascina qui o clicca (max 5 immagini)")}
                          </div>
                          <input type="file" accept="image/*" className="hidden" {...imgUpload.inputProps} data-testid="upload-img" />
                        </label>
                      ) : (
                        <div className="text-xs text-slate1">Limite massimo raggiunto (5).</div>
                      )}
                      <UploadProgressBar progress={imgUpload.progress} />
                      <UploadDetailedProgress perFile={imgUpload.perFile} />
                    </div>

                    {/* Disegni tecnici PDF (visibili a tutti gli utenti loggati) */}
                    <div className="pt-4 border-t border-ink/10 mt-4">
                      <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">Disegni tecnici / Schede ({(pEditing.files || []).filter(f => !f.admin_only).length})</div>
                      <div className="text-[10px] text-slate1 mb-3">Carica PDF o documenti con disegni tecnici, schede prodotto, esplosi. Visibili a tutti.</div>
                      <div className="space-y-2 mb-3">
                        {(pEditing.files || []).filter(f => !f.admin_only).map((f) => (
                          <div key={f.id} className="flex items-center gap-3 p-2.5 bg-cream2 border border-ink/10 rounded-sm" data-testid={`product-file-${f.id}`}>
                            <FileText size={14} className="text-slate1 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-ink truncate">{f.title || f.filename}</div>
                              <div className="text-[10px] text-slate1 truncate">{f.filename} · {(f.size / 1024).toFixed(0)} KB</div>
                            </div>
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/products/files/${f.id}/preview`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate1 hover:text-ink"
                              title="Anteprima"
                            ><Eye size={14} /></a>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Eliminare "${f.title || f.filename}"?`)) return;
                                try {
                                  await api.delete(`/products/${pEditing.id}/files/${f.id}`);
                                  setPEditing({ ...pEditing, files: (pEditing.files || []).filter((x) => x.id !== f.id) });
                                  reload();
                                  toast.success("File eliminato");
                                } catch { toast.error("Errore eliminazione"); }
                              }}
                              className="text-slate1 hover:text-destructive"
                              title="Elimina"
                              data-testid={`del-file-${f.id}`}
                            ><Trash2 size={14} /></button>
                          </div>
                        ))}
                      </div>
                      <label
                        className={`flex flex-col gap-1 items-center justify-center px-4 py-3 border-2 border-dashed ${docUpload.dragOver ? "border-ink bg-cream2" : "border-ink/30"} rounded-sm cursor-pointer hover:bg-cream2 transition-colors text-[11px] tracking-[0.2em] uppercase text-ink`}
                        {...docUpload.handlers}
                      >
                        <Upload size={13} />
                        <span>
                          {docUpload.uploading
                            ? (docUpload.progress ? `Caricamento ${docUpload.progress.done}/${docUpload.progress.total}…` : "Caricamento...")
                            : (docUpload.dragOver ? "Rilascia qui i file" : "Trascina qui o clicca · disegni / schede / PDF")}
                        </span>
                        <input
                          type="file"
                          accept="application/pdf,image/*,.dwg,.dxf"
                          className="hidden"
                          data-testid="product-file-upload-input"
                          {...docUpload.inputProps}
                        />
                      </label>
                      <UploadProgressBar progress={docUpload.progress} />
                      <UploadDetailedProgress perFile={docUpload.perFile} />
                    </div>

                    {/* Documenti admin riservati (fatture, conferme d'ordine, disegni interni) */}
                    <div className="pt-4 border-t border-rose-200 mt-4 bg-rose-50/30 -mx-4 px-4 pb-4 rounded-sm">
                      <div className="text-[11px] tracking-[0.2em] uppercase text-rose-700 mb-2 inline-flex items-center gap-2">
                        <span className="w-2 h-2 bg-rose-500 rounded-full"></span>
                        Documenti admin riservati ({(pEditing.files || []).filter(f => f.admin_only).length})
                      </div>
                      <div className="text-[10px] text-slate1 mb-3">
                        Fatture di acquisto, conferme d&apos;ordine, disegni interni, contratti.{" "}
                        <strong className="text-rose-700">Visibili e scaricabili SOLO ad admin.</strong>{" "}
                        Non appaiono nel PDF prodotto né nelle schede pubbliche.
                      </div>
                      <div className="space-y-2 mb-3">
                        {(pEditing.files || []).filter(f => f.admin_only).map((f) => (
                          <div key={f.id} className="flex items-center gap-3 p-2.5 bg-white border border-rose-200 rounded-sm" data-testid={`product-admin-file-${f.id}`}>
                            <FileText size={14} className="text-rose-600 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-ink truncate">{f.title || f.filename}</div>
                              <div className="text-[10px] text-slate1 truncate">
                                {f.filename} · {(f.size / 1024).toFixed(0)} KB
                                {f.uploaded_by && ` · caricato da ${f.uploaded_by}`}
                              </div>
                            </div>
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/products/files/${f.id}/preview`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate1 hover:text-ink"
                              title="Anteprima"
                            ><Eye size={14} /></a>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Eliminare il documento riservato "${f.title || f.filename}"?`)) return;
                                try {
                                  await api.delete(`/products/${pEditing.id}/files/${f.id}`);
                                  setPEditing({ ...pEditing, files: (pEditing.files || []).filter((x) => x.id !== f.id) });
                                  reload();
                                  toast.success("Documento riservato eliminato");
                                } catch { toast.error("Errore eliminazione"); }
                              }}
                              className="text-slate1 hover:text-destructive"
                              title="Elimina"
                              data-testid={`del-admin-file-${f.id}`}
                            ><Trash2 size={14} /></button>
                          </div>
                        ))}
                      </div>
                      <label
                        className={`flex flex-col gap-1 items-center justify-center px-4 py-3 border-2 border-dashed ${adminDocUpload.dragOver ? "border-rose-500 bg-rose-50" : "border-rose-300"} rounded-sm cursor-pointer hover:bg-rose-50 transition-colors text-[11px] tracking-[0.2em] uppercase text-rose-700`}
                        {...adminDocUpload.handlers}
                      >
                        <Upload size={13} />
                        <span>
                          {adminDocUpload.uploading
                            ? (adminDocUpload.progress ? `Caricamento ${adminDocUpload.progress.done}/${adminDocUpload.progress.total}…` : "Caricamento...")
                            : (adminDocUpload.dragOver ? "Rilascia qui i documenti riservati" : "Trascina o clicca · fatture, conferme, contratti (max 50MB)")}
                        </span>
                        <input
                          type="file"
                          accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.dwg,.dxf"
                          className="hidden"
                          data-testid="product-admin-file-upload-input"
                          {...adminDocUpload.inputProps}
                        />
                      </label>
                      <UploadProgressBar progress={adminDocUpload.progress} />
                      <UploadDetailedProgress perFile={adminDocUpload.perFile} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          </>
        )}

        {tab === "banners" && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={createBanner} className="bg-white border border-ink/10 p-6 rounded-sm space-y-3" data-testid="admin-banner-form">
              <div className="font-display text-lg text-ink">Nuovo banner</div>
              <input required placeholder="Titolo" value={bForm.title} onChange={(e) => setBForm({ ...bForm, title: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              <input placeholder="Sottotitolo" value={bForm.subtitle} onChange={(e) => setBForm({ ...bForm, subtitle: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              <div className="border border-dashed border-ink/20 rounded-sm p-4">
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-2">Immagine banner</label>
                {(bForm.image_id || bForm.image_url) && (
                  <div className="relative mb-3">
                    <img
                      src={bForm.image_id ? `${process.env.REACT_APP_BACKEND_URL}/api/banners/images/${bForm.image_id}` : bForm.image_url}
                      alt="anteprima"
                      className="w-full h-32 object-cover rounded-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setBForm((p) => ({ ...p, image_id: null, image_url: "" }))}
                      className="absolute top-1.5 right-1.5 bg-ink/80 text-cream text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-sm"
                      data-testid="admin-banner-image-remove"
                    >
                      Rimuovi
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => { uploadBannerImage(e.target.files?.[0]); e.target.value = ""; }}
                  className="w-full text-sm"
                  data-testid="admin-banner-image-upload"
                />
                <div className="mt-2 text-[10px] tracking-[0.15em] uppercase text-slate1">oppure URL esterna</div>
                <input
                  placeholder="https://..."
                  value={bForm.image_id ? "" : bForm.image_url}
                  disabled={!!bForm.image_id}
                  onChange={(e) => setBForm({ ...bForm, image_url: e.target.value, image_id: null })}
                  className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream disabled:opacity-50"
                  data-testid="admin-banner-image-url"
                />
              </div>
              <input placeholder="Link" value={bForm.link} onChange={(e) => setBForm({ ...bForm, link: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              <select value={bForm.kind} onChange={(e) => setBForm({ ...bForm, kind: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="admin-banner-kind">
                {BANNER_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              {banners.length >= MAX_BANNERS && (
                <div className="text-[10px] tracking-[0.15em] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm" data-testid="admin-banner-max-warning">
                  Limite di {MAX_BANNERS} banner raggiunto. Elimina un banner per crearne un altro.
                </div>
              )}
              <button disabled={banners.length >= MAX_BANNERS} className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="admin-create-banner">Crea Banner ({banners.length}/{MAX_BANNERS})</button>
            </form>

            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              {banners.map(b => (
                <div key={b.id} className="relative rounded-sm overflow-hidden" data-testid={`admin-banner-${b.id}`}>
                  <img src={b.image_id ? `${process.env.REACT_APP_BACKEND_URL}/api/banners/images/${b.image_id}` : b.image_url} alt={b.title} className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-ink/60" />
                  <div className="absolute inset-0 p-4 text-cream flex flex-col justify-between">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-cream/70">{BANNER_KIND_LABEL[b.kind] || b.kind}</div>
                    <div>
                      <div className="font-display text-lg">{b.title}</div>
                      <div className="text-xs text-cream/70">{b.subtitle}</div>
                    </div>
                  </div>
                  <button onClick={() => delBanner(b.id)} className="absolute top-2 right-2 p-2 bg-cream/90 rounded-sm text-ink" data-testid={`admin-del-banner-${b.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "flash" && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={createFlash} className="bg-white border border-ink/10 p-6 rounded-sm space-y-3" data-testid="admin-flash-form">
              <div className="font-display text-lg text-ink">Nuova Flash Sale</div>
              <select required value={fForm.product_id} onChange={(e) => setFForm({ ...fForm, product_id: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream">
                <option value="">Seleziona prodotto</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.brand} — {p.name}</option>)}
              </select>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Sconto %</label>
              <input required type="number" min={1} max={90} value={fForm.discount_pct} onChange={(e) => setFForm({ ...fForm, discount_pct: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Fine</label>
              <input required type="datetime-local" value={fForm.ends_at} onChange={(e) => setFForm({ ...fForm, ends_at: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Stock</label>
              <input required type="number" value={fForm.stock} onChange={(e) => setFForm({ ...fForm, stock: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
              <button className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="admin-create-flash">Crea Flash</button>
            </form>

            <div className="lg:col-span-2 divide-y divide-ink/10 bg-white border border-ink/10 rounded-sm">
              {flash.map(s => (
                <div key={s.id} className="p-4 flex items-center gap-4" data-testid={`admin-flash-${s.id}`}>
                  <img src={s.product?.image_url} alt={s.product?.name} className="w-16 h-16 object-cover rounded-sm" />
                  <div className="flex-1">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{s.product?.brand}</div>
                    <div className="text-ink">{s.product?.name}</div>
                    <div className="text-xs text-slate1">-{s.discount_pct}% · Stock {s.stock} · Fino {new Date(s.ends_at).toLocaleString("it-IT")}</div>
                  </div>
                  <button onClick={() => delFlash(s.id)} className="text-slate1 hover:text-destructive" data-testid={`admin-del-flash-${s.id}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "home" && <div className="mt-8"><AdminBriefing /></div>}
        {tab === "approvals" && <div className="mt-8"><AdminApprovals /></div>}
        {tab === "users" && <div className="mt-8"><AdminUsers /></div>}
        {tab === "stats" && <div className="mt-8"><AdminStats /></div>}
        {tab === "fic-stats" && <div className="mt-8"><AdminFicStats /></div>}
        {tab === "ai-rules" && <div className="mt-8"><AdminAiRules /></div>}
        {tab === "share-links" && <AdminShareLinks />}
        {tab === "projects" && <div className="mt-8"><AdminProjects accountType="studio" /></div>}
        {tab === "projects-trader" && <div className="mt-8"><AdminProjects accountType="trader" /></div>}
        {tab === "contacts" && <div className="mt-8"><AdminContacts /></div>}
        {tab === "assets" && <div className="mt-8"><AdminAssets /></div>}
        {tab === "website-finder" && <div className="mt-8"><SupplierWebsiteFinder embedded /></div>}
        {tab === "audit-downloads" && <div className="mt-8"><AdminAuditDownloads /></div>}
        {tab === "dropbox-docs" && <div className="mt-8"><AdminCollegamenti /></div>}
        {tab === "installers" && <div className="mt-8"><AdminInstallers /></div>}
        {tab === "store-insight" && <StoreInsight />}
      </div>

      {cropTarget && (
        <CropImageModal
          productId={cropTarget.pid}
          imageId={cropTarget.imgId}
          imageUrl={cropTarget.url}
          onClose={() => setCropTarget(null)}
          onSaved={async () => {
            setCropTarget(null);
            setImgCacheBust(Date.now());
            try {
              const fresh = (await api.get(`/products/${cropTarget.pid}`)).data;
              setPEditing(fresh);
              reload();
            } catch { /* no-op */ }
          }}
        />
      )}
    </div>
  );
}
